import { useCallback } from 'react';

import { Typography } from 'shared';

import { VARIANTS } from 'shared/components/Button';
import {
  modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  SIZES,
  useModalContext,
} from 'shared/components/Modal';
import { ModalButton } from 'shared/components/Modal/ModalButton';

type ConfirmationModalProps = {
  title: string;
  content: React.ReactNode;
  onConfirm: () => void;
  confirmBtnText?: string;
  shouldClose?: boolean;
};

export const useConfirmationModal = ({
  title,
  content,
  onConfirm,
  confirmBtnText = 'Delete',
  shouldClose = true,
}: ConfirmationModalProps) =>
  useCallback(() => {
    modal.open({
      content: <ConfirmationModalContent>{content}</ConfirmationModalContent>,
      props: { size: SIZES.SM },
      context: {
        onConfirm,
        title,
        confirmBtnText,
        shouldClose,
      },
    });
  }, [title, content, onConfirm, confirmBtnText, shouldClose]);

const ConfirmationModalContent = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { title, onConfirm, confirmBtnText, shouldClose } =
    useModalContext() as ConfirmationModalProps;

  return (
    <>
      <ModalHeader>
        <Typography as="h3" type="heading" fontWeight="semibold" color="black">
          {title}
        </Typography>
      </ModalHeader>
      <ModalBody>{children}</ModalBody>
      <ModalFooter>
        <ModalButton variant="outline">Cancel</ModalButton>
        <ModalButton
          onClick={onConfirm}
          color={VARIANTS.CRITICAL}
          shouldClose={shouldClose}
        >
          {confirmBtnText}
        </ModalButton>
      </ModalFooter>
    </>
  );
};
